import React, { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  authHintClass,
  authInputClass,
  authLabelClass,
  authPrimaryButtonClass,
  authSecondaryButtonClass,
  type AuthFormTone,
} from './authFormStyles';

type LoginFormProps = {
  onMfaRequired: () => void;
  onError: (message: string) => void;
  tone?: AuthFormTone;
};

function LoginForm({ onMfaRequired, onError, tone = 'light' }: LoginFormProps) {
  const { login, loginWithPasskey } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [passkeySubmitting, setPasskeySubmitting] = React.useState(false);
  const emailLooksValid = React.useMemo(() => {
    const v = email.trim();
    return v.length >= 3 && v.includes('@');
  }, [email]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    onError('');

    try {
      const result = await login({ email, password });
      if (result.mfaRequired && onMfaRequired) {
        onMfaRequired();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('auth.login_error_default', { defaultValue: 'Unable to sign in.' });
      onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasskeySubmit = async () => {
    if (passkeySubmitting) return;
    if (!emailLooksValid) {
      onError(
        t('auth.passkey_email_required', {
          defaultValue: 'Enter your email first (passkeys are linked to your email).',
        }),
      );
      return;
    }
    setPasskeySubmitting(true);
    onError('');
    try {
      await loginWithPasskey(email.trim());
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('auth.passkey_login_failed', { defaultValue: 'Passkey login failed.' });
      onError(message);
    } finally {
      setPasskeySubmitting(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div className="space-y-1.5">
        <label htmlFor="email" className={authLabelClass(tone)}>
          {t('auth.email_label')}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          className={authInputClass(tone)}
          placeholder={t('auth.email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className={authHintClass(tone)}>
          {t('auth.passkey_hint_email_first', {
            defaultValue: 'To use a passkey, enter your email first.',
          })}
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="password" className={authLabelClass(tone)}>
          {t('auth.password_label')}
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          className={authInputClass(tone)}
          placeholder={t('auth.password_mask_placeholder', {
            defaultValue: '••••••••',
          })}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <div className="space-y-2 pt-1">
        <button type="submit" disabled={submitting} className={authPrimaryButtonClass(tone)}>
          {submitting
            ? t('auth.signing_in_state', { defaultValue: 'Signing in…' })
            : t('auth.sign_in_button')}
        </button>
        <button
          type="button"
          onClick={handlePasskeySubmit}
          disabled={passkeySubmitting || !emailLooksValid}
          className={authSecondaryButtonClass(tone)}
        >
          {passkeySubmitting
            ? t('auth.passkey_waiting_state', {
                defaultValue: 'Waiting for passkey...',
              })
            : t('auth.passkey_continue_button', {
                defaultValue: 'Continue with passkey',
              })}
        </button>
      </div>
    </form>
  );
}

export default LoginForm;
